import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { Award, Coins, Save, Pencil, ChevronDown, ChevronRight, Eye, EyeOff, Gift, Plus, Loader2, RefreshCw, Wallet } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import AdminLayout from './AdminLayout';
import { BadgeEditDialog } from '@/components/admin/BadgeEditDialog';
import type { KudozAwardAction, Badge as BadgeType } from '@shared/schema';

const BADGE_CATEGORIES = [
  { key: 'loyalty',          label: 'Loyalty',              description: 'Repeat visits to restaurants' },
  { key: 'kudoz',            label: 'Kudoz Earned',          description: 'Lifetime Kudoz accumulation' },
  { key: 'review',           label: 'Reviews',               description: 'Number of reviews submitted' },
  { key: 'engagement',       label: 'Engagement',            description: 'App activity and sessions' },
  { key: 'social',           label: 'Social',                description: 'Friends and connections' },
  { key: 'community',        label: 'Community',             description: 'Followers and platform influence' },
  { key: 'order_diversity',  label: 'Order Diversity',       description: 'Unique restaurants ordered from' },
  { key: 'review_diversity', label: 'Review Diversity',      description: 'Unique restaurants reviewed' },
  { key: 'profile',          label: 'Profile',               description: 'Profile completion' },
  { key: 'trust',            label: 'Trust',                 description: 'Verified and trusted user' },
  { key: 'tip',              label: 'Tip Generosity',        description: 'Hidden surprise badges for generous tippers' },
];

const KUDOZ_CATEGORIES = ['order', 'review', 'profile', 'service', 'badge'];

const kudozActionSchema = z.object({
  actionType:          z.string().min(1, 'Action type is required').regex(/^[a-z0-9_]+$/, 'Use lowercase letters, numbers and underscores only'),
  displayName:         z.string().min(1, 'Display name is required').max(100),
  description:         z.string().max(500).optional(),
  category:            z.string().min(1, 'Category is required'),
  useFormula:          z.boolean(),
  baseAmount:          z.coerce.number().int().min(0).optional(),
  calculationFormula:  z.string().optional(),
  isActive:            z.boolean(),
});

type KudozActionFormData = z.infer<typeof kudozActionSchema>;

export default function AdminGamification() {
  const { toast } = useToast();
  const [editingAction, setEditingAction] = useState<string | null>(null);
  const [editingAmount, setEditingAmount] = useState<number>(0);
  const [editingBadge, setEditingBadge] = useState<BadgeType | null>(null);
  const [isCreateBadgeOpen, setIsCreateBadgeOpen] = useState(false);
  const [isCreateActionOpen, setIsCreateActionOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(BADGE_CATEGORIES.map(c => c.key)));

  const { data: platformWallet, isLoading: walletLoading } = useQuery<{
    id: string; type: string; ownerId: string | null; balance: number;
  }>({
    queryKey: ['/api/admin/platform-wallet'],
  });

  const { data: kudozActions, isLoading: actionsLoading } = useQuery<KudozAwardAction[]>({
    queryKey: ['/api/admin/kudoz-actions'],
  });

  const { data: badges, isLoading: badgesLoading } = useQuery<BadgeType[]>({
    queryKey: ['/api/admin/badges'],
  });

  const badgesByCategory = useMemo(() => {
    if (!badges) return {};
    const grouped: Record<string, BadgeType[]> = {};
    for (const badge of badges) {
      if (!grouped[badge.category]) grouped[badge.category] = [];
      grouped[badge.category].push(badge);
    }
    for (const category in grouped) {
      grouped[category].sort((a, b) => {
        const tierOrder = { bronze: 1, silver: 2, gold: 3, platinum: 4 };
        return (tierOrder[a.tier as keyof typeof tierOrder] || 0) - (tierOrder[b.tier as keyof typeof tierOrder] || 0);
      });
    }
    return grouped;
  }, [badges]);

  const updateActionMutation = useMutation({
    mutationFn: async ({ id, baseAmount, isActive }: { id: string; baseAmount?: number; isActive?: boolean }) => {
      return apiRequest('PATCH', `/api/admin/kudoz-actions/${id}`, { baseAmount, isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/kudoz-actions'] });
      setEditingAction(null);
      toast({ title: 'Updated', description: 'Kudoz action has been updated' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update action', variant: 'destructive' });
    },
  });

  const actionForm = useForm<KudozActionFormData>({
    resolver: zodResolver(kudozActionSchema),
    defaultValues: {
      actionType:         '',
      displayName:        '',
      description:        '',
      category:           'review',
      useFormula:         false,
      baseAmount:         10,
      calculationFormula: '',
      isActive:           true,
    },
  });

  const createActionMutation = useMutation({
    mutationFn: async (data: KudozActionFormData) => {
      return apiRequest('POST', '/api/admin/kudoz-actions', {
        actionType:         data.actionType,
        displayName:        data.displayName,
        description:        data.description || null,
        category:           data.category,
        baseAmount:         data.useFormula ? null : data.baseAmount,
        calculationFormula: data.useFormula ? data.calculationFormula : null,
        isActive:           data.isActive,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/kudoz-actions'] });
      setIsCreateActionOpen(false);
      actionForm.reset();
      toast({ title: 'Kudoz action created' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create action', variant: 'destructive' });
    },
  });

  const handleSaveAmount = (id: string) => updateActionMutation.mutate({ id, baseAmount: editingAmount });
  const handleToggleActive = (id: string, isActive: boolean) => updateActionMutation.mutate({ id, isActive });

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category); else next.add(category);
      return next;
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'order':   return 'bg-green-500';
      case 'review':  return 'bg-blue-500';
      case 'service': return 'bg-purple-500';
      case 'profile': return 'bg-orange-500';
      case 'badge':   return 'bg-yellow-500';
      default:        return 'bg-gray-500';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze':   return 'bg-amber-600 text-white';
      case 'silver':   return 'bg-gray-400 text-white';
      case 'gold':     return 'bg-yellow-500 text-black';
      case 'platinum': return 'bg-purple-500 text-white';
      default:         return 'bg-gray-500 text-white';
    }
  };

  const getBadgeCategoryAccentColor = (category: string) => {
    switch (category) {
      case 'loyalty':          return 'bg-rose-500';
      case 'kudoz':            return 'bg-amber-500';
      case 'review':           return 'bg-blue-500';
      case 'engagement':       return 'bg-green-500';
      case 'social':           return 'bg-pink-500';
      case 'community':        return 'bg-indigo-500';
      case 'order_diversity':  return 'bg-cyan-500';
      case 'review_diversity': return 'bg-violet-500';
      case 'profile':          return 'bg-orange-500';
      case 'trust':            return 'bg-emerald-500';
      case 'tip':              return 'bg-purple-500';
      default:                 return 'bg-gray-500';
    }
  };

  const renderBadgeIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || LucideIcons.Award;
    return <Icon className="w-5 h-5" />;
  };

  const useFormula = actionForm.watch('useFormula');

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Gamification Settings</h1>
          <p className="text-muted-foreground">Manage Kudoz rewards and badge configurations</p>
        </div>

        {/* ── Eatzy Platform Wallet ── */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-muted-foreground" />
              <div>
                <CardTitle className="text-base">Eatzy Platform Wallet</CardTitle>
                <CardDescription className="text-xs">Primary Kudoz funding source for user rewards</CardDescription>
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/platform-wallet'] })}
              data-testid="button-refresh-platform-wallet"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {walletLoading ? (
              <div className="flex gap-8">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-10 w-48" />
              </div>
            ) : platformWallet ? (
              <div className="flex flex-wrap gap-8">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Wallet ID</p>
                  <p className="font-mono text-sm" title={platformWallet.id}>
                    {platformWallet.id.slice(0, 8)}…{platformWallet.id.slice(-4)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Balance</p>
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-amber-500" />
                    <span className="text-xl font-bold tabular-nums">
                      {platformWallet.balance.toLocaleString()}
                    </span>
                    <span className="text-sm text-muted-foreground">Kudoz</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Type</p>
                  <Badge variant="secondary">{platformWallet.type}</Badge>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Platform wallet not found. It will be created on next server startup.</p>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="kudoz">
          <TabsList>
            <TabsTrigger value="kudoz" data-testid="tab-kudoz">
              <Coins className="w-4 h-4 mr-2" />
              Kudoz Actions
            </TabsTrigger>
            <TabsTrigger value="badges" data-testid="tab-badges">
              <Award className="w-4 h-4 mr-2" />
              Badges ({badges?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* ── Kudoz Actions Tab ── */}
          <TabsContent value="kudoz" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button onClick={() => { actionForm.reset(); setIsCreateActionOpen(true); }} data-testid="button-add-kudoz-action">
                <Plus className="w-4 h-4 mr-2" />
                Add Action
              </Button>
            </div>

            {actionsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
                ))}
              </div>
            ) : (
              kudozActions?.map((action) => (
                <Card key={action.id} data-testid={`card-action-${action.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{action.displayName}</span>
                          <Badge className={getCategoryColor(action.category)}>{action.category}</Badge>
                          {!action.isActive && <Badge variant="outline">Inactive</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{action.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Type: {action.actionType}
                          {action.calculationFormula && ` | Formula: ${action.calculationFormula}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {editingAction === action.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={editingAmount}
                              onChange={(e) => setEditingAmount(parseInt(e.target.value) || 0)}
                              className="w-24"
                            />
                            <Button size="sm" onClick={() => handleSaveAmount(action.id)}>
                              <Save className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={() => { setEditingAction(action.id); setEditingAmount(action.baseAmount || 0); }}
                          >
                            <Coins className="w-4 h-4 mr-2" />
                            {action.baseAmount ?? 'Formula'}
                          </Button>
                        )}
                        <Switch
                          checked={action.isActive ?? true}
                          onCheckedChange={(checked) => handleToggleActive(action.id, checked)}
                          data-testid={`switch-active-${action.id}`}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* ── Badges Tab ── */}
          <TabsContent value="badges" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button onClick={() => setIsCreateBadgeOpen(true)} data-testid="button-add-badge">
                <Plus className="w-4 h-4 mr-2" />
                Add Badge
              </Button>
            </div>

            {badgesLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}><CardContent className="p-4"><Skeleton className="h-32 w-full" /></CardContent></Card>
                ))}
              </div>
            ) : (
              BADGE_CATEGORIES.map((categoryInfo) => {
                const categoryBadges = badgesByCategory[categoryInfo.key] || [];
                if (categoryBadges.length === 0) return null;

                const isExpanded = expandedCategories.has(categoryInfo.key);
                const isHiddenCategory = categoryInfo.key === 'tip';

                return (
                  <Collapsible
                    key={categoryInfo.key}
                    open={isExpanded}
                    onOpenChange={() => toggleCategory(categoryInfo.key)}
                  >
                    <Card>
                      <CollapsibleTrigger asChild>
                        <CardHeader className="cursor-pointer hover-elevate py-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <div className={`w-1 h-8 rounded-full ${getBadgeCategoryAccentColor(categoryInfo.key)}`} />
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-muted-foreground" />
                              )}
                              <div>
                                <CardTitle className="text-base flex items-center gap-2">
                                  {categoryInfo.label}
                                  {isHiddenCategory && (
                                    <Badge variant="outline" className="text-xs">
                                      <Gift className="w-3 h-3 mr-1" />
                                      Hidden
                                    </Badge>
                                  )}
                                </CardTitle>
                                <CardDescription className="text-xs">{categoryInfo.description}</CardDescription>
                              </div>
                            </div>
                            <Badge variant="secondary">{categoryBadges.length} badges</Badge>
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {categoryBadges.map((badge) => (
                              <div
                                key={badge.id}
                                className={`border rounded-lg p-3 relative group ${!badge.isActive ? 'opacity-50' : ''}`}
                                data-testid={`card-badge-${badge.id}`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className={`p-1.5 rounded ${getTierColor(badge.tier)}`}>
                                      {renderBadgeIcon(badge.iconName)}
                                    </div>
                                    <div>
                                      <div className="font-medium text-sm">{badge.name}</div>
                                      <Badge variant="outline" className="text-xs capitalize">{badge.tier}</Badge>
                                    </div>
                                  </div>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => setEditingBadge(badge)}
                                    data-testid={`button-edit-badge-${badge.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                </div>

                                <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{badge.description}</p>

                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">
                                    {badge.requirementType.replace(/_/g, ' ')}: {badge.thresholdValue}
                                  </span>
                                  <span className="font-medium text-amber-600">+{badge.bonusKudoz}</span>
                                </div>

                                <div className="flex items-center gap-2 mt-2 pt-2 border-t flex-wrap">
                                  {!badge.isActive && (
                                    <Badge variant="outline" className="text-xs">
                                      <EyeOff className="w-3 h-3 mr-1" />
                                      Inactive
                                    </Badge>
                                  )}
                                  {badge.isHidden && (
                                    <Badge variant="outline" className="text-xs">
                                      <Gift className="w-3 h-3 mr-1" />
                                      Surprise
                                    </Badge>
                                  )}
                                  {!badge.displayInProfile && badge.isActive && !badge.isHidden && (
                                    <Badge variant="outline" className="text-xs">
                                      <Eye className="w-3 h-3 mr-1" />
                                      Hidden from profile
                                    </Badge>
                                  )}
                                  {badge.isActive && !badge.isHidden && badge.displayInProfile && (
                                    <Badge variant="outline" className="text-xs text-green-600">
                                      <Eye className="w-3 h-3 mr-1" />
                                      Active
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Edit Badge Dialog ── */}
      <BadgeEditDialog
        badge={editingBadge}
        open={!!editingBadge}
        onOpenChange={(open) => !open && setEditingBadge(null)}
        existingBadges={badges || []}
      />

      {/* ── Create Badge Dialog ── */}
      <BadgeEditDialog
        badge={null}
        open={isCreateBadgeOpen}
        onOpenChange={setIsCreateBadgeOpen}
        existingBadges={badges || []}
      />

      {/* ── Create Kudoz Action Dialog ── */}
      <Dialog open={isCreateActionOpen} onOpenChange={setIsCreateActionOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              New Kudoz Action
            </DialogTitle>
            <DialogDescription>
              Define a new user action that awards Kudoz points.
            </DialogDescription>
          </DialogHeader>

          <Form {...actionForm}>
            <form onSubmit={actionForm.handleSubmit((d) => createActionMutation.mutate(d))} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={actionForm.control}
                  name="actionType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Action Type</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., review_video" data-testid="input-action-type" />
                      </FormControl>
                      <FormDescription>Unique snake_case identifier</FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={actionForm.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-action-category">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {KUDOZ_CATEGORIES.map(c => (
                            <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={actionForm.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., Add Video to Review" data-testid="input-action-name" />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={actionForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="What does this action reward?" rows={2} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={actionForm.control}
                name="useFormula"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div>
                      <FormLabel className="cursor-pointer">Use Formula Instead of Fixed Amount</FormLabel>
                      <FormDescription>e.g., 7 * orderTotal</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {useFormula ? (
                <FormField
                  control={actionForm.control}
                  name="calculationFormula"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Formula</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 7 * orderTotal" data-testid="input-formula" />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={actionForm.control}
                  name="baseAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Kudoz Amount</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" min={0} data-testid="input-base-amount" />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={actionForm.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div>
                      <FormLabel className="cursor-pointer">Active</FormLabel>
                      <FormDescription>Award Kudoz for this action immediately</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} data-testid="switch-action-active" />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateActionOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createActionMutation.isPending} data-testid="button-save-action">
                  {createActionMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Create Action
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
